import { motion } from "framer-motion";
import { useLang } from "../../context/LanguageContext";
import { institutionalPoints, images } from "../../data/content";

const ease = [0.16, 1, 0.3, 1];

const Institutional = () => {
  const { isAr, t } = useLang();

  return (
    <section
      className="relative bg-bg-ivory py-24 md:py-36 overflow-hidden border-t border-brand-green/5"
      data-testid="institutional-section"
    >
      {/* Blueprint Grid Mesh Background */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none" 
        style={{ 
          backgroundImage: `linear-gradient(rgba(23,63,53,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(23,63,53,0.15) 1px, transparent 1px)`, 
          backgroundSize: "80px 80px" 
        }} 
      />

      {/* Decorative Blueprint Corner Marks */}
      <div className="absolute top-8 left-8 w-4 h-4 border-t border-l border-brand-green/15 pointer-events-none" />
      <div className="absolute top-8 right-8 w-4 h-4 border-t border-r border-brand-green/15 pointer-events-none" />
      <div className="absolute bottom-8 left-8 w-4 h-4 border-b border-l border-brand-green/15 pointer-events-none" />
      <div className="absolute bottom-8 right-8 w-4 h-4 border-b border-r border-brand-green/15 pointer-events-none" />

      <div className="relative z-10 max-w-container mx-auto px-6 md:px-10 lg:px-16">
        
        {/* Top Mini Meta Header */}
        <div 
          className="flex justify-between items-center text-brand-green/40 mb-16 border-b border-brand-green/10 pb-6"
          dir={isAr ? "rtl" : "ltr"}
        >
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs tracking-widest uppercase text-brand-green/60">
              [ {t("الاعتماد المؤسسي", "INSTITUTIONAL PARTNERSHIP")} ]
            </span>
          </div>
          <div className="flex items-center gap-6">
            <span className="font-mono text-xs tracking-widest hidden md:inline">BB // SAUDI VISION 2030</span>
            <span className="font-mono text-xs">22 — SEC</span>
          </div>
        </div>

        {/* Section Intro Title */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease }}
          className={`mb-20 max-w-4xl ${isAr ? "text-right" : "text-left"}`}
          dir={isAr ? "rtl" : "ltr"}
        >
          <div className={`${isAr ? "label-meta-ar" : "label-meta"} text-brand-green/50 mb-6 tracking-widester`}>
            {t("✦ الشراكة الاستراتيجية", "✦ STRATEGIC COLLABORATION")}
          </div>
          <h3
            className={`${isAr ? "font-arabic font-semibold" : "font-serif"} text-brand-green leading-[1.1] tracking-tight`}
            style={{ fontSize: isAr ? "clamp(36px, 4.5vw, 64px)" : "clamp(46px, 5vw, 82px)" }}
          >
            {isAr ? (
              <>
                الجاهزية المؤسسية <br />
                <span className="font-light italic text-brand-green/85">والشراكة الصناعية الاستراتيجية</span>
              </>
            ) : (
              <>
                Institutional Readiness <br />
                <span className="font-light italic text-brand-green/80">& Strategic Partnership</span>
              </>
            )}
          </h3>
        </motion.div>

        {/* Main Content Layout */}
        <div className="grid grid-cols-12 gap-x-8 gap-y-16 lg:gap-x-16 items-start">
          
          {/* Column 1: Image Showcase */}
          <motion.div 
            initial={{ opacity: 0, x: isAr ? 40 : -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease }}
            className={`col-span-12 lg:col-span-6 xl:col-span-7 ${isAr ? "lg:order-2" : "lg:order-1"}`}
          >
            {/* Elegant double lined editorial frame */}
            <div className="relative border border-brand-green/10 p-3 bg-white/20 backdrop-blur-sm shadow-sm group">
              <div className="absolute inset-0 border border-brand-green/5 -m-2 pointer-events-none" />
              
              <div className="relative overflow-hidden h-[450px] md:h-[580px]">
                <img
                  src={images.modernInterior}
                  alt={t("شراكة استراتيجية", "Strategic Partnership")}
                  className="w-full h-full object-cover transition-transform duration-[1.8s] ease-out group-hover:scale-105"
                  style={{ filter: "contrast(1.02) saturate(0.9) brightness(0.97)" }}
                />
                
                {/* Soft luxury shadow overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-brand-green/40 via-transparent to-transparent opacity-70 mix-blend-multiply pointer-events-none" />

                {/* Subtitle labels inside image */}
                <div className="absolute top-6 left-6 right-6 flex justify-between items-center text-white/90 z-20" dir={isAr ? "rtl" : "ltr"}>
                  <span className="font-mono text-[10px] tracking-widest uppercase bg-brand-green/60 backdrop-blur-md px-3 py-1 text-white border border-white/10">
                    — STANDARDS
                  </span>
                  <span className="font-mono text-[10px] tracking-widest uppercase bg-white/10 backdrop-blur-md px-3 py-1 text-white border border-white/5">
                    {t("شريك معتمد", "CERTIFIED PARTNER")}
                  </span>
                </div>

                <div className="absolute bottom-8 left-8 right-8 text-white z-20" dir={isAr ? "rtl" : "ltr"}>
                  <div className="font-mono text-xs uppercase tracking-widest text-brand-champagne/80 mb-2">
                    {t("نموذج الجودة الفاخرة", "Luxury Quality Model")}
                  </div>
                  <h4 className={`text-2xl md:text-3xl font-light ${isAr ? "font-arabic" : "font-serif italic"}`}>
                    {t("معايير صناعية وطنية", "National Industrial Standards")}
                  </h4>
                </div>
              </div>
            </div>

            {/* Subtle engineering notes below the photo */}
            <div 
              className="mt-6 flex justify-between text-[11px] font-mono text-brand-green/45 uppercase tracking-wider"
              dir={isAr ? "rtl" : "ltr"}
            >
              <span>SYS.REF: 902-INSTITUTIONAL</span>
              <span>SCALE: 1.00 // BB-HQ</span>
            </div>
          </motion.div>

          {/* Column 2: Text & Premium List */}
          <motion.div 
            initial={{ opacity: 0, x: isAr ? -40 : 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease, delay: 0.15 }}
            className={`col-span-12 lg:col-span-6 xl:col-span-5 flex flex-col justify-start ${isAr ? "lg:order-1" : "lg:order-2"}`}
          >
            {/* Elegant description with thick brand border */}
            <div 
              className={`relative ${isAr ? "pr-6 pl-0 border-r-2 border-brand-green/30" : "pl-6 pr-0 border-l-2 border-brand-green/30"} mb-12`}
              dir={isAr ? "rtl" : "ltr"}
            >
              <p 
                className={`text-brand-green/95 leading-relaxed text-[17px] md:text-[19px] font-light ${isAr ? "font-arabic text-right" : "font-sans text-left"}`}
                style={{ lineHeight: isAr ? 1.85 : 1.7 }}
              >
                {t(
                  "تعمل بيج براند وفق نموذج مؤسسي منظم يتوافق مع متطلبات الجهات الحكومية والمطورين الاستراتيجيين والمشاريع الكبرى في كافة مراحل التوريد والتصنيع.",
                  "BigBrand operates according to a highly disciplined, organized institutional model aligned with the exact requirements of government entities, strategic developers, and major projects across all supply and manufacturing phases."
                )}
              </p>
            </div>

            {/* Premium Interactive List */}
            <div className="flex flex-col w-full" dir={isAr ? "rtl" : "ltr"}>
              {institutionalPoints.map((p, i) => (
                <div
                  key={i}
                  className="group relative flex items-center justify-between py-6 border-b border-brand-green/10 transition-all duration-500 ease-out cursor-pointer hover:px-4 hover:bg-brand-green/[0.015]"
                >
                  {/* Dynamic background expand bar */}
                  <div className={`absolute bottom-0 ${isAr ? "right-0" : "left-0"} h-[1px] w-0 bg-brand-green transition-all duration-500 group-hover:w-full`} />

                  <div className="flex items-center gap-6">
                    {/* Numbers with modern serif typography */}
                    <span 
                      className={`font-serif text-[28px] md:text-[34px] leading-none font-light tracking-tight text-brand-green/20 transition-all duration-500 group-hover:text-brand-green group-hover:scale-105 origin-center
                        ${isAr ? "group-hover:-translate-x-1" : "group-hover:translate-x-1"}`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    
                    {/* Label */}
                    <span 
                      className={`text-[17px] md:text-[18px] text-brand-green/80 group-hover:text-brand-green font-medium transition-all duration-300
                        ${isAr ? "font-arabic group-hover:-translate-x-1" : "font-sans group-hover:translate-x-1"}`}
                      style={{ transitionProperty: "color, transform" }}
                    >
                      {isAr ? p.ar : p.en}
                    </span>
                  </div>

                  {/* Elegant Mono Badge sliding in */}
                  <div className={`transform transition-all duration-500 ease-out opacity-0 ${isAr ? "translate-x-4 group-hover:translate-x-0" : "-translate-x-4 group-hover:translate-x-0"} group-hover:opacity-100`}>
                    <span className="font-mono text-[10px] text-brand-green/50 bg-brand-green/5 border border-brand-green/10 px-2 py-0.5 tracking-wider rounded-none">
                      {t("معتمد", "APPROVED")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Institutional;
